import React, { useEffect, useState, memo } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Collapse, Container, Row, Input } from 'reactstrap';
import { ExecutionInputType, QuestionType } from '../types'
import { copyTextToClipboard } from "../utils/copyToClipboard";
import Toastr from "toastr";
import { ExpandMore, ExpandLess, LiveHelp } from "@material-ui/icons";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const SingleQuestion: React.FC<{
  question: QuestionType;
  mapping?: Record<string, string>;
  setSearchTerm: (term: string) => void;
  setFinalSearchTerm: (term: string) => void;
  setTxtCollapsed: (collapsed: boolean) => void;
  setActiveExtractedTextIdx: (idx: number) => void;
  setCurrentDocumentPage: (page: number) => void;
  defaultOpen: boolean;
  inputType: ExecutionInputType;
}> = ({
  question,
  mapping,
  setFinalSearchTerm,
  setSearchTerm,
  setTxtCollapsed,
  setActiveExtractedTextIdx,
  setCurrentDocumentPage,
  defaultOpen = false,
  inputType,
}) => {
    const [activeSource, setActiveSource] = useState(0);
    const [collapsed, setCollapsed] = useState(!defaultOpen);
    const [showRedactedReference, setShowRedactedReference] = useState(false)
    const [refrenceSearch, setRefrenceSearch] = useState<string>("");

    const [filteredSources, setFilteredSources] = useState<{
      value: string;
      index: number;
      original_value: string;
      page_no: number;
      url: string;
    }[]>([]);

    const submitSearchReferences = (e: any) =>{
      e.preventDefault();
      searchReferences();
    }

    function getValidSources():({
        value: string;
        index: number;
        original_value: string;
        page_no: number;
        url: string;
    }[] | undefined){
      if(!question.qnaResponse){
        return;
      }

      if(!question.qnaResponse.sources || question.qnaResponse.sources.length === 0 ){
        return;
      }

      if(!question.qnaResponse.sources[activeSource]){
        return;
      }

      return [...question.qnaResponse.sources[activeSource]];
    }

    useEffect(() => {
      let sources = getValidSources();
      setFilteredSources(sources ? sources: []);
      searchReferences();
    }, [activeSource]);

    const searchReferences = () => {
      let sources = getValidSources();

      if(!sources){
        return;
      }

      // if no search string, set filtered to default and return
      if(!refrenceSearch){
        setFilteredSources([...sources]);
        return;
      }
      
      // filter sources according to search string
      let filtered = sources.filter((source) => (source.value.search(new RegExp(refrenceSearch, "i")) !== -1));
      // initiliase an array for highlited records
      let highlited:{
        value: string;
        index: number;
        original_value: string;
        page_no: number;
        url: string;
      }[] = [];

      for(let source of filtered){
        let highlitedSource:{
          value: string;
          index: number;
          original_value: string;
          page_no: number;
          url: string;
        } = {...source};

        // replace value with highlited value
        highlitedSource.value = highlitedSource.value.replace(new RegExp(refrenceSearch, "ig"), `<span style="background-color: #fb6340; color: #fff; border-radius: 3px; padding: 0 5px; line-height: 1.8em; letter-spacing: .08em; font-weight: 600; opacity: .9;">${refrenceSearch}</span>`)
        // push to hilighted array
        highlited.push(highlitedSource);
      }
      
      // finally set filtered sources
      setFilteredSources([...highlited]);
    };

    const handleCopyAllSources = () => {
      if (!question.qnaResponse || !question.qnaResponse.sources || !question.qnaResponse.sources[activeSource]) return

      const sources = question.qnaResponse.sources[activeSource]
      copyTextToClipboard(sources.map((source) => source.value).join("\n"))
      Toastr.success("Copied to clipboard")
    }

    const handleCopyAllOutput = () => {
      if (!question.qnaResponse || !question.qnaResponse.responses) return

      const responses = question.qnaResponse.responses
      copyTextToClipboard(responses.join("\n"))
      Toastr.success("Copied to clipboard")
    }

    return (
      <Container fluid className='mb-4'>
        <Card className="shadow">
          <CardHeader>
            <h4 style={{ display: 'inline' }}><LiveHelp className='text-success' /> {question.question}</h4>
            <span className="float-right">
              <Button size="sm" color="primary" onClick={() => setCollapsed(!collapsed)}>
                {collapsed ? <ExpandMore /> : <ExpandLess />}
              </Button>
            </span>
            {
              question.timeTakenInSeconds &&
              (
                <>
                  <br />
                  <span style={{
                    fontSize: "0.8rem",
                  }}>Time taken: {question.timeTakenInSeconds} seconds {(question.callFrom !== 'openllm') && <>and cost: <span>${(question.cost || 0).toFixed(4)}</span></>} {(question.callFrom && question.callFrom !== 'infinity') && <>and source: <span>{question.callFrom === 'openllm' ? 'Internal LLM' : question.callFrom}</span></>}</span>&nbsp;
                </>
              )
            }
          </CardHeader>
          <Collapse isOpen={!collapsed}>
            <CardBody>
              <Row>
                <Col className="order-xl-1">
                  {/* <Row>
                  <Col
                    xs="6"
                    className="offset-3"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter the question here"
                      value={question.question}
                    />
                  </Col>
                </Row> */}
                  {
                    question?.qnaResponse?.responses &&
                    question?.qnaResponse?.responses?.length > 0 &&
                    <Row className="ques-resp-col">
                      <Col
                        xs="6"
                        className="ques-resp-col"
                        style={{
                          overflowY: "scroll",
                          maxHeight: "350px",
                        }}
                      >
                        <h2
                          className="row-h2"
                          style={{
                            position: "sticky",
                            top: "0",
                            background: "white",
                          }}
                        >
                          Output&nbsp;
                          <small>
                            (
                            {(Array.isArray(question?.qnaResponse?.responses) ? question?.qnaResponse?.responses : []).reduce((acc, curr) => acc + curr).split(' ').filter(w => w.length > 0).length} words
                            )
                          </small>
                          {
                            question.qnaResponse.responses &&
                            question.qnaResponse.responses.length > 0 &&
                            <small className="ml-2">
                              <i
                                className="fas fa-copy"
                                onClick={handleCopyAllOutput}
                                style={{ cursor: 'pointer' }}
                              />
                            </small>
                          }
                        </h2>
                        <ul>
                          {question.qnaResponse.responses
                            .length > 0 &&
                            question.qnaResponse.responses.map(
                              (item, index) => {
                                let newText = item;
                                // reverse mapping
                                for (const [key, val] of Object.entries(mapping || {})) {
                                  newText = newText.replace(
                                    new RegExp(val, "ig"),
                                    `${key}`
                                  );
                                }
                                return (
                                  <li
                                    className="li-resp-col"
                                    style={{
                                      textDecoration: activeSource === index ? "none" : "none",
                                      letterSpacing: ".02em",
                                      lineHeight: "1.8em"
                                    }}
                                    key={index}
                                    onClick={() => {
                                      setActiveSource(index);
                                    }}
                                  >
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{newText}</ReactMarkdown>
                                  </li>
                                );
                              }
                            )}
                        </ul>
                      </Col>
                      <Col
                        xs="6"
                        className="ques-resp-col"
                        style={{
                          overflowY: "scroll",
                          maxHeight: "350px",
                        }}
                      >
                        <h2
                          className="row-h2"
                          style={{
                            position: "sticky",
                            top: "0",
                            background: "white",
                            zIndex: "10"
                          }}
                        >
                          References&nbsp;
                          <small>
                            (
                              {(filteredSources.length === 0 ? [{ value: '' }] : filteredSources).map(c => c.value).reduce((acc, curr) => acc + curr).split(' ').filter(w => w.length > 0).length} words
                            )
                          </small>
                          {
                            filteredSources &&
                            filteredSources.length > 0 &&
                            <small className="ml-2">
                              <i
                                className="fas fa-copy"
                                onClick={handleCopyAllSources}
                                style={{ cursor: 'pointer' }}
                              />
                            </small>
                          }
                          {
                            question?.redactedHtml &&
                            <>
                              &nbsp;<Button className="btn-warning" onClick={() => setShowRedactedReference(!showRedactedReference)} size="sm">Show {!showRedactedReference ? "Redacted" : "Text"}</Button>
                            </>
                          }
                        </h2>
                        {!showRedactedReference && <form style={{ marginLeft: '40px', marginRight: '20px'}} onSubmit={(e) => submitSearchReferences(e)}>
                          <Input placeholder='search for references..' onChange={(e) => setRefrenceSearch(e.target.value)} />
                        </form>}
                        {!showRedactedReference && <ul>
                          {filteredSources.length ?
                            filteredSources.map((item, idx) => (
                              <li
                                className="li-source-col"
                                key={idx}
                              >
                                <p
                                  style={{
                                    cursor: "pointer",
                                    wordSpacing: ".08em",
                                    letterSpacing: ".02em",
                                    lineHeight: "1.8em",
                                    fontWeight: "500",
                                    display: "inline"
                                  }}
                                  onClick={() => {
                                    setSearchTerm(item.original_value);
                                    setFinalSearchTerm(item.original_value);
                                    setActiveExtractedTextIdx(item.index);
                                    setCurrentDocumentPage(item.page_no);
                                    document
                                      .querySelector("#txt_section")
                                      ?.scrollIntoView({
                                        behavior: "smooth",
                                      });
                                    setTxtCollapsed(false);
                                  }}

                                  dangerouslySetInnerHTML={{
                                    __html: item.value
                                  }}
                                >
                                  {/* {item.value} */}
                                </p>
                                {
                                  inputType === ExecutionInputType.file &&
                                  <Button
                                    size="sm"
                                    color="primary"
                                    className="ml-2"
                                    onClick={() => {
                                      // TODO: have token appended in react query hook (data can be transformed using `select` option)
                                      window.open(`${item.url}?token=${localStorage.getItem('token')}#page=${item.page_no + 1}`, "_blank")
                                    }}
                                  >
                                    Open File
                                  </Button>
                                }
                              </li>
                            )): "No match found"}
                        </ul>}

                        {
                          showRedactedReference && <ul>
                            <li style={{
                              cursor: "pointer", letterSpacing: ".02em",
                              lineHeight: "1.8em"
                            }}
                              dangerouslySetInnerHTML={{
                                __html: question?.redactedHtml,
                              }}
                            />
                          </ul>
                        }
                      </Col>
                    </Row>
                  }
                </Col>
              </Row>
            </CardBody>
          </Collapse>
        </Card>
      </Container>
    )
  }

export default memo(SingleQuestion);
